import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarBlank, CaretLeft, CaretRight, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuthGate } from "@/lib/hooks/useAuthGate";
import SessionExpiredState from "@/components/auth/SessionExpiredState";
import TeacherTimeOffPanel from "@/components/TeacherTimeOffPanel";
import { endOfWeek, getStaffTimeOffSnapshot, listMyShifts, startOfWeek } from "@/lib/scheduling";

/** Teacher · /teacher/schedule — read-only view of assigned staff shifts. */
export default function TeacherSchedule() {
  const { status, profile } = useAuthGate();
  const [weekDate, setWeekDate] = useState(() => new Date());
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeOff, setTimeOff] = useState(null);
  const [timeOffLoading, setTimeOffLoading] = useState(true);
  const [timeOffError, setTimeOffError] = useState(null);

  const weekStart = useMemo(() => startOfWeek(weekDate), [weekDate]);
  const weekEnd = useMemo(() => endOfWeek(weekDate), [weekDate]);

  useEffect(() => {
    if (status !== "ready" || !profile?.id) {
      if (status === "session_expired") setLoading(false);
      return undefined;
    }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const rows = await listMyShifts(profile.id, {
          from: weekStart.toISOString(),
          to: weekEnd.toISOString(),
        });
        if (alive) setShifts(rows);
      } catch (err) {
        if (alive) toast.error(err?.message || "Could not load your schedule");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [status, profile?.id, weekStart, weekEnd]);

  const refreshTimeOff = useCallback(async () => {
    if (status !== "ready" || !profile?.id) {
      if (status === "session_expired") setTimeOffLoading(false);
      return;
    }
    setTimeOffLoading(true);
    setTimeOffError(null);
    try {
      setTimeOff(await getStaffTimeOffSnapshot());
    } catch (err) {
      setTimeOffError(err?.message || "Could not load time off");
    } finally {
      setTimeOffLoading(false);
    }
  }, [status, profile?.id]);

  useEffect(() => {
    refreshTimeOff();
  }, [refreshTimeOff]);

  if (status === "session_expired") {
    return <SessionExpiredState scope="schedule" testId="teacher-schedule-session-expired" />;
  }

  const days = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + index);
    return date;
  });
  const shiftsByDay = new Map();
  for (const shift of shifts) {
    const key = new Date(shift.starts_at).toLocaleDateString("en-CA");
    const rows = shiftsByDay.get(key) || [];
    rows.push(shift);
    shiftsByDay.set(key, rows);
  }

  return (
    <div data-testid="teacher-schedule-page" className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Staff</p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          Schedule
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-neutral-600">
          Your assigned classroom shifts for the week. Ask your admin to make changes.
        </p>
      </header>

      <TeacherTimeOffPanel
        snapshot={timeOff}
        loading={timeOffLoading}
        error={timeOffError}
        onRefresh={refreshTimeOff}
      />

      <section className="rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CalendarBlank size={18} weight="duotone" className="text-primary-hover" />
            <p data-testid="teacher-schedule-week-label" className="font-heading text-base font-semibold text-neutral-900">
              {formatRange(weekStart, weekEnd)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="teacher-schedule-prev-week" onClick={() => shiftWeek(setWeekDate, -1)} aria-label="Previous week" className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-600 hover:bg-neutral-50">
              <CaretLeft size={15} weight="bold" />
            </button>
            <button type="button" data-testid="teacher-schedule-this-week" onClick={() => setWeekDate(new Date())} className="rounded-full border border-neutral-200 px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50">
              This week
            </button>
            <button type="button" data-testid="teacher-schedule-next-week" onClick={() => shiftWeek(setWeekDate, 1)} aria-label="Next week" className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-600 hover:bg-neutral-50">
              <CaretRight size={15} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        </div>
      ) : shifts.length === 0 ? (
        <div data-testid="teacher-schedule-empty" className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center">
          <CalendarBlank size={30} weight="duotone" className="mx-auto text-neutral-400" />
          <p className="mt-3 font-heading text-base font-semibold text-neutral-900">No shifts scheduled</p>
          <p className="mt-1 text-sm text-neutral-500">Your admin can add a shift from Scheduling.</p>
        </div>
      ) : (
        <ol data-testid="teacher-schedule-list" className="space-y-3">
          {days.map((day) => {
            const key = day.toLocaleDateString("en-CA");
            const rows = shiftsByDay.get(key) || [];
            return (
              <li key={key} data-testid={`teacher-schedule-day-${key}`} className={`rounded-2xl border bg-white p-4 ${isToday(day) ? "border-primary/40" : "border-neutral-200"}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <p className="font-heading text-base font-semibold text-neutral-900">{day.toLocaleDateString(undefined, { weekday: "long" })}</p>
                  <p className="text-xs text-neutral-500">{day.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p>
                </div>
                {rows.length === 0 ? (
                  <p className="mt-2 text-xs text-neutral-400">No shift</p>
                ) : (
                  <ul className="mt-3 space-y-2">
                    {rows.map((shift) => (
                      <li key={shift.id} data-testid={`teacher-schedule-shift-${shift.id}`} className="flex items-start gap-3 rounded-xl bg-neutral-50 px-3 py-2.5">
                        <Clock size={16} weight="duotone" className="mt-0.5 shrink-0 text-primary-hover" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-neutral-900">{formatTime(shift.starts_at)} – {formatTime(shift.ends_at)}</p>
                          <p className="text-xs text-neutral-600">{shift.classrooms?.name || shift.role_label || "Staff shift"}</p>
                          {shift.notes && <p className="mt-0.5 text-xs text-neutral-500">{shift.notes}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function shiftWeek(setWeekDate, amount) {
  setWeekDate((current) => {
    const next = new Date(current);
    next.setDate(next.getDate() + amount * 7);
    return next;
  });
}

function isToday(date) {
  return date.toDateString() === new Date().toDateString();
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function formatRange(start, end) {
  const last = new Date(end);
  last.setDate(last.getDate() - 1);
  return `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${last.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
}
